import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

interface ChatWidgetMessage {
  title: string;
  description: string;
  cta: string;
}

export type ChatSize = "compact" | "medium" | "full";

interface ChatWidgetStore {
  interactiveMessage: ChatWidgetMessage | null;
  showInteractiveMessage: boolean;
  isChatOpen: boolean;
  /** compact (default, 2-3 messages) -> medium -> full. */
  chatSize: ChatSize;
  /** Where the user dragged the compact chat, as an offset from its corner. */
  chatOffset: { x: number; y: number };
  setInteractiveMessage: (message: ChatWidgetMessage | null) => void;
  hideInteractiveMessage: () => void;
  showMessage: () => void;
  setChatOpen: (open: boolean) => void;
  setChatSize: (size: ChatSize) => void;
  setChatOffset: (offset: { x: number; y: number }) => void;
}

const noopStorage: Storage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
  clear: () => {},
  key: () => null,
  length: 0,
};

export const useChatWidgetStore = create<ChatWidgetStore>()(
  persist(
    (set) => ({
      interactiveMessage: {
        title: "",
        description: "",
        cta: "",
      },
      showInteractiveMessage: true,
      isChatOpen: false,
      chatSize: "compact",
      chatOffset: { x: 0, y: 0 },

      setInteractiveMessage: (message) =>
        set({
          interactiveMessage: message,
          showInteractiveMessage: message !== null,
        }),

      hideInteractiveMessage: () => set({ showInteractiveMessage: false }),

      showMessage: () => set({ showInteractiveMessage: true }),

      setChatOpen: (open) => set({ isChatOpen: open }),

      setChatSize: (size) => set({ chatSize: size }),

      setChatOffset: (offset) => set({ chatOffset: offset }),
    }),
    {
      // Open/closed and expanded/shrunk carry across page loads and into the
      // new tab a listing opens (localStorage; sessionStorage is per tab).
      name: "oh-chat-widget",
      storage: createJSONStorage(() =>
        typeof window !== "undefined" ? localStorage : noopStorage,
      ),
      partialize: (state) => ({
        isChatOpen: state.isChatOpen,
        chatSize: state.chatSize,
        chatOffset: state.chatOffset,
      }),
    },
  ),
);
